/**
 * Centralized API Client
 * Ensures all API calls use the configured BASE_URL from environment variables
 * This fixes the 404 errors on production by routing all requests through the proper backend URL
 */

import { API_CONFIG, buildApiUrl } from './config'

interface RequestOptions extends RequestInit {
  skipAuth?: boolean
  timeout?: number
}

/**
 * Centralized fetch wrapper that ensures proper URL construction
 */
export const apiClient = async (
  endpoint: string,
  options: RequestOptions = {}
): Promise<Response> => {
  const { skipAuth = false, timeout = 30000, ...fetchOptions } = options

  // Ensure endpoint starts with /
  const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`
  
  // Build the full URL using the configured BASE_URL
  const fullUrl = buildApiUrl(normalizedEndpoint)

  // Build mutable headers object to safely set fields regardless of HeadersInit variations
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }

  // Merge fetchOptions.headers into our headers object regardless of its shape
  if (fetchOptions.headers) {
    const fh = fetchOptions.headers
    if (fh instanceof Headers) {
      fh.forEach((value, key) => {
        headers[key] = value
      })
    } else if (Array.isArray(fh)) {
      for (const [key, value] of fh) {
        headers[key] = value
      }
    } else {
      Object.assign(headers, fh)
    }
  }

  // Add auth token if not skipped
  if (!skipAuth && typeof window !== 'undefined') {
    const token = localStorage.getItem('token')
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }
  }

  const config: RequestInit = {
    ...fetchOptions,
    headers,
  }

  try {
    // Create abort controller for timeout
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), timeout)

    const response = await fetch(fullUrl, {
      ...config,
      signal: controller.signal,
    })

    clearTimeout(timeoutId)

    // Handle authentication errors
    if (response.status === 401 && !skipAuth) {
      if (typeof window !== 'undefined') {
        localStorage.removeItem('token')
        localStorage.removeItem('user')
        window.location.href = '/user/login'
      }
    }

    return response
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error('Request timeout')
    }
    console.error('API Client Error:', error)
    throw error
  }
}

/**
 * GET request
 */
export const apiGet = (endpoint: string, options?: RequestOptions) =>
  apiClient(endpoint, { ...options, method: 'GET' })

/**
 * POST request
 */
export const apiPost = (endpoint: string, data?: any, options?: RequestOptions) =>
  apiClient(endpoint, {
    ...options,
    method: 'POST',
    body: data ? JSON.stringify(data) : undefined,
  })

/**
 * PUT request
 */
export const apiPut = (endpoint: string, data?: any, options?: RequestOptions) =>
  apiClient(endpoint, {
    ...options,
    method: 'PUT',
    body: data ? JSON.stringify(data) : undefined,
  })

/**
 * PATCH request
 */
export const apiPatch = (endpoint: string, data?: any, options?: RequestOptions) =>
  apiClient(endpoint, {
    ...options,
    method: 'PATCH',
    body: data ? JSON.stringify(data) : undefined,
  })

/**
 * DELETE request
 */
export const apiDelete = (endpoint: string, options?: RequestOptions) =>
  apiClient(endpoint, { ...options, method: 'DELETE' })

/**
 * Helper to parse JSON response safely
 */
export const parseResponse = async <T>(response: Response): Promise<T> => {
  const contentType = response.headers.get('content-type')
  if (contentType?.includes('application/json')) {
    return response.json()
  }
  throw new Error('Invalid response format')
}

/**
 * Helper to handle API errors
 */
export const handleApiError = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message
  }
  return 'An unexpected error occurred'
}

/**
 * API call with automatic error handling
 */
export const apiCall = async <T>(
  endpoint: string,
  options?: RequestOptions
): Promise<{ data?: T; error?: string; status: number }> => {
  try {
    const response = await apiClient(endpoint, options)
    const data = await response.json()

    if (!response.ok) {
      return {
        error: data.message || `HTTP ${response.status}`,
        status: response.status,
      }
    }

    return {
      data,
      status: response.status,
    }
  } catch (error) {
    return {
      error: handleApiError(error),
      status: 0,
    }
  }
}
